import { useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, Instagram, Twitter, Mail, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const genCaptcha = () => {
  const a = Math.floor(Math.random() * 9) + 1; // 1..9
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b, answer: a + b };
};

const Contact = () => {
  const { t, isRTL } = useLanguage();
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(() => genCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");

  const refreshCaptcha = useCallback(() => {
    setCaptcha(genCaptcha());
    setCaptchaInput("");
  }, []);

  const captchaPrompt = useMemo(() => `${captcha.a} + ${captcha.b} = ?`, [captcha]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    // anti-spam: verify math answer
    const parsed = Number.parseInt(captchaInput, 10);
    if (Number.isNaN(parsed) || parsed !== captcha.answer) {
      toast.error(t.contact.captchaError);
      refreshCaptcha();
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
      refreshCaptcha();
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative section-y bg-ink border-t border-white/5">
      <Toaster position={isRTL ? "top-left" : "top-right"} theme="dark" richColors />
      <div className="container-x">
        <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline" />
              <span className="overline">{t.contact.overline}</span>
            </div>
            <h2
              className={`${isRTL ? "font-arabic font-black leading-[1.15]" : "font-serif leading-[1.05]"} text-bone text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance`}
              data-testid="contact-heading"
            >
              {t.contact.heading}
            </h2>
            <p className={`mt-6 text-white/60 max-w-md ${isRTL ? "font-arabic text-lg leading-[1.9]" : ""}`}>
              {t.contact.sub}
            </p>

            <div className="mt-12 pt-8 border-t border-white/10">
              <div className={`overline mb-5 ${isRTL ? "font-arabic tracking-normal text-sm" : ""}`}>{t.contact.direct}</div>
              <div className="flex items-center gap-5">
                <a
                  href="mailto:contact@albakertv.com"
                  data-testid="social-email"
                  className="text-white/60 hover:text-gold transition-colors"
                  aria-label="Email"
                >
                  <Mail size={20} strokeWidth={1.25} />
                </a>
                <a
                  href="https://twitter.com/albakertv"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-twitter"
                  className="text-white/60 hover:text-gold transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter size={20} strokeWidth={1.25} />
                </a>
                <a
                  href="https://instagram.com/albakertv"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-instagram"
                  className="text-white/60 hover:text-gold transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={20} strokeWidth={1.25} />
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 space-y-8"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <Field
                label={t.contact.fields.name}
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                testId="contact-input-name"
                isRTL={isRTL}
                required
              />
              <Field
                label={t.contact.fields.email}
                type="email"
                value={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                testId="contact-input-email"
                isRTL={isRTL}
                required
              />
            </div>
            <Field
              label={t.contact.fields.subject}
              value={form.subject}
              onChange={(v) => setForm((f) => ({ ...f, subject: v }))}
              testId="contact-input-subject"
              isRTL={isRTL}
            />
            <Field
              label={t.contact.fields.message}
              as="textarea"
              rows={5}
              value={form.message}
              onChange={(v) => setForm((f) => ({ ...f, message: v }))}
              testId="contact-input-message"
              isRTL={isRTL}
              required
            />

            {/* Anti-spam simple math captcha */}
            <div className="flex items-end gap-4 sm:gap-6">
              <div className="flex-1">
                <label className="block">
                  <span className={`block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 ${isRTL ? "font-arabic tracking-normal text-sm" : ""}`}>
                    {t.contact.fields.captcha}
                  </span>
                  <div className="flex items-center gap-4">
                    <span
                      data-testid="captcha-prompt"
                      className="font-serif text-gold text-2xl sm:text-3xl select-none tabular-nums"
                      style={{ direction: "ltr" }}
                    >
                      {captchaPrompt}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                      data-testid="contact-input-captcha"
                      className="editorial-input max-w-[120px] text-center tabular-nums"
                      style={{ direction: "ltr" }}
                      required
                    />
                  </div>
                </label>
              </div>
              <button
                type="button"
                onClick={refreshCaptcha}
                data-testid="captcha-refresh"
                aria-label="Refresh"
                className="mb-3 text-white/50 hover:text-gold transition-colors"
              >
                <RefreshCw size={18} strokeWidth={1.25} />
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="btn-gold group disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span>{loading ? t.contact.sending : t.contact.send}</span>
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, as = "input", type = "text", rows, value, onChange, testId, isRTL, required }) => {
  const base = `editorial-input ${isRTL ? "font-arabic text-base" : ""}`;
  return (
    <label className="block">
      <span className={`block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 ${isRTL ? "font-arabic tracking-normal text-sm" : ""}`}>
        {label}{required ? " *" : ""}
      </span>
      {as === "textarea" ? (
        <textarea
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className={`${base} resize-none`}
          required={required}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className={base}
          required={required}
        />
      )}
    </label>
  );
};

export default Contact;
